import { useState } from 'react'
import type { FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { friendlyAuthError, signIn } from '@/data/auth'
import { AuthLayout, FormError } from './AuthLayout'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    if (!email.trim() || !password) {
      setError('Enter your email and password.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await signIn(email, password)
      // Nothing more to do: the session changes, and the router sends the person
      // to the right place (first-login, their department, or the overview).
    } catch (err) {
      setError(friendlyAuthError(err))
      setBusy(false)
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="Use the email and password the church treasurer gave you.">
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {error && <FormError>{error}</FormError>}
        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          autoFocus
        />
        <div className="flex flex-col gap-2">
          <Input
            label="Password"
            type={show ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
          <label className="flex items-center gap-2 text-body text-muted min-h-[44px] select-none">
            <input
              type="checkbox"
              className="size-5 rounded border-line-strong"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
            />
            Show password
          </label>
        </div>
        <Button type="submit" size="lg" isLoading={busy} className="w-full">
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="text-body text-muted text-center">
          Forgot your password? Ask the church treasurer or an administrator to reset it for you.
        </p>
      </form>
    </AuthLayout>
  )
}
